// reducers/chatSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  onTextChat,
  fetchChatThreads,
  fetchChatHistoryFromSessionApi,
} from "../asyncApi/chat";
import { persistSessionMeetingEntry } from "@/utils/sessionMeetingMap";

/* ---------------------- Types ---------------------- */

export interface FollowUpQuestion {
  text: string;
  category: string;
  target_agent: string;
  confidence: number;
  grounding_source: string[];
}

// Chat message interface representing individual chat messages
export interface ChatMessage {
  key: string;
  role: "user" | "bot";
  content?: string;
  question?: string;
  thought?: string;
  greeting?: string;
  type?: string;
  complete?: boolean;
  images?: any; // can be base64 map / url / array
  messageId?: string;
  documentSearchResults?: any;
  answers?: any[];
  showDocumentSelection?: boolean;
  searchResults?: any;
  showSiteSelection?: boolean;
  message_id?: string;
  answer_id?: string;
  collectionNames?: string[]; // populated from mapInteractionsToMessages for collection_chat
  collectionLabel?: string;
  /** Parent collection name(s) when collectionNames are file names (file-level chat). */
  collectionContextNames?: string[];
  isCollectionChat?: boolean;
  genie_message_id?: string; // For employee_360 messages
  /** Meeting-notes chat: shown above user bubble */
  meetingName?: string;
  /** SSE followup_questions chips for this assistant turn */
  followUpQuestions?: FollowUpQuestion[];
}

// New interface for threads metadata
export interface ChatThread {
  session_id: string;
  session_name: string;
  is_active?: boolean;
  /** For meeting-note chats: backend may return real session_id + meeting_id; we use meeting_id to fetch history */
  meeting_id?: string;
  /** Full list for POST /v1/meeting-notes/chat when the API returns meeting_ids */
  meeting_ids?: string[];
  /** Backend session metadata for API routing when opened from Recent Chats */
  type?: string;
  source?: string;
  chatType?: string;
  meeting_name?: string;
}

// Pair used for collection chips
export interface CollectionPair {
  id: string;
  name?: string;
}

export interface CollectionFileItem {
  id: string;
  name: string;
}

export interface ChatState {
  loading: boolean;
  error: string | null;
  messages: Record<string, ChatMessage[]>; // Maps session_id to list of messages
  threads: Record<string, ChatThread>; // Maps session_id to thread metadata
  sessionId: string | null;
  showThought: Record<string, boolean>;
  pendingNewChat: boolean;
    popup: { visible: boolean; message: string } | null;
  sessionCollections: Record<string, CollectionPair[]>; // { [sessionId]: [{id,name}] }
  /** Selected library file UUIDs per session for POST .../collections/chat (collection_file_ids). */
  sessionCollectionFileIds: Record<string, string[]>;
  /** Selected library files per session for rendering file-name chips in the chat input. */
  sessionCollectionFileItems: Record<string, CollectionFileItem[]>;
  loadedSessions: Record<string, boolean>; // which sessions were fetched at least once
  /** Incremented when user re-selects the same chat from Recent Chats so HomeScreen can refetch history. */
  recentChatReselectNonce: number;
}

/* ---------------------- Initial State ---------------------- */

const initialState: ChatState = {
  loading: false,
  error: null,
  messages: {},
  threads: {},
  sessionId: null,
  showThought: {},
  pendingNewChat: false,
  popup: null,
  sessionCollections: {},
  sessionCollectionFileIds: {},
  sessionCollectionFileItems: {},
  loadedSessions: {},
  recentChatReselectNonce: 0,
};

/* ---------------------- Slice ---------------------- */

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addMessage: (
      state,
      action: PayloadAction<{
        key: string;
        role: "user" | "bot";
        question?: string;
        thought?: string;
        greeting?: string;
        sessionId?: string | null;
        collectionNames?: string[];
        collectionLabel?: string;
        collectionContextNames?: string[];
        type?: string; // ✅ new
        isCollectionChat?: boolean; // ✅ new
        meetingName?: string;
      }>
    ) => {
      const sid = action.payload.sessionId ?? state.sessionId;
      if (!sid) return;
      if (!Array.isArray(state.messages[sid])) state.messages[sid] = [];
      state.messages[sid].push({
        key: action.payload.key,
        role: action.payload.role,
        question: action.payload.question,
        thought: action.payload.thought,
        greeting: action.payload.greeting,
        collectionNames: action.payload.collectionNames,
        collectionLabel: action.payload.collectionLabel,
        ...(Array.isArray(action.payload.collectionContextNames) &&
        action.payload.collectionContextNames.length > 0
          ? { collectionContextNames: action.payload.collectionContextNames }
          : {}),
        type: action.payload.type,
        isCollectionChat: action.payload.isCollectionChat, // ✅ store
        ...(action.payload.meetingName?.trim()
          ? { meetingName: action.payload.meetingName.trim() }
          : {}),
      });
      if (action.payload.role === "bot") state.loading = true;
      state.error = null;
      if (action.payload.role === "bot" && action.payload.thought) {
        state.showThought[action.payload.key] = true;
      }
    },

    setPendingNewChat: (state, action: PayloadAction<boolean>) => {
      state.pendingNewChat = action.payload;
    },

    setShowThoughtTrue: (state, action: PayloadAction<string>) => {
      state.showThought[action.payload] = true;
    },

    setShowThought: (
      state,
      action: PayloadAction<{ key: string; visible: boolean }>
    ) => {
      state.showThought[action.payload.key] = action.payload.visible;
    },

    toggleShowThought: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      state.showThought[key] = !state.showThought[key];
    },

    clearShowThought: (state) => {
      state.showThought = {};
    },

    addUntitledThread: (
      state,
      action: PayloadAction<Record<string, ChatThread>>
    ) => {
      const validThreads = Object.entries(action.payload).reduce<
        Record<string, ChatThread>
      >((acc, [sessionId, thread]) => {
        if (!sessionId || !sessionId.trim() || !thread) return acc;
        acc[sessionId] = {
          ...thread,
          session_id: thread.session_id || sessionId,
        };
        return acc;
      }, {});
      if (Object.keys(validThreads).length === 0) return;
      state.threads = {
        ...validThreads,
        ...state.threads,
      };
    },

    renameThread: (
      state,
      action: PayloadAction<{ session_id: string; session_name: string }>
    ) => {
      const { session_id, session_name } = action.payload;
      if (state.threads[session_id]) {
        state.threads[session_id] = {
          ...state.threads[session_id],
          session_name,
        };
      }
    },

    deleteThread: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      if (state.threads[sessionId]) {
        delete state.threads[sessionId];
      }
      if (state.messages[sessionId]) {
        delete state.messages[sessionId];
      }
      if (state.sessionCollections[sessionId]) {
        delete state.sessionCollections[sessionId];
      }
      if (state.sessionCollectionFileIds[sessionId]) {
        delete state.sessionCollectionFileIds[sessionId];
      }
      if (state.sessionCollectionFileItems[sessionId]) {
        delete state.sessionCollectionFileItems[sessionId];
      }
      if (state.loadedSessions[sessionId]) {
        delete state.loadedSessions[sessionId];
      }
    },

    appendToBotMessage: (
      state,
      action: PayloadAction<{
        key: string;
        content?: string;
        thought?: string;
        question?: string;
        greeting?: string;
        type?: string;
        sessionId?: string | null;
      }>
    ) => {
      const sid = action.payload.sessionId ?? state.sessionId;
      if (!sid || !Array.isArray(state.messages[sid])) return;
      const msg = state.messages[sid].find(
        (m) => m.key === action.payload.key && m.role === "bot"
      );
      if (msg) {
        if (action.payload.thought)
          msg.thought = (msg.thought ?? "") + action.payload.thought;
        state.showThought[msg.key] = true;
        if (action.payload.question)
          msg.question = (msg.question ?? "") + action.payload.question;
        if (action.payload.greeting)
          msg.greeting = (msg.greeting ?? "") + action.payload.greeting;
        if (typeof action.payload.content === "string")
          msg.content = (msg.content ?? "") + action.payload.content;
        if (action.payload.type) msg.type = action.payload.type;
      }
    },

    markMessageComplete: (
      state,
      action: PayloadAction<{
        key: string;
        images?: any;
        messageId?: string;
        sessionId?: string | null;
      }>
    ) => {
      const sid = action.payload.sessionId ?? state.sessionId;
      if (!sid || !Array.isArray(state.messages[sid])) return;
      const msg = state.messages[sid].find(
        (m) => m.key === action.payload.key && m.role === "bot"
      );
      if (msg) {
        msg.complete = true;
        if (action.payload.images) msg.images = action.payload.images;
        if (action.payload.messageId) msg.messageId = action.payload.messageId;
      }
      state.loading = false;
    },

    setDocumentSearchResults: (
      state,
      action: PayloadAction<{
        key: string;
        results: any;
        messageId?: string;
        sessionId?: string | null;
      }>
    ) => {
      const sid = action.payload.sessionId ?? state.sessionId;
      if (!sid || !Array.isArray(state.messages[sid])) return;
      const msg = state.messages[sid].find(
        (m) => m.key === action.payload.key && m.role === "bot"
      );
      if (msg) {
        msg.documentSearchResults = Array.isArray(action.payload.results)
          ? action.payload.results
          : Array.isArray(action.payload.results?.results)
          ? action.payload.results.results
          : [];
        msg.showDocumentSelection = true;
        if (action.payload.messageId) msg.messageId = action.payload.messageId;
        msg.complete = true;
      }
      state.loading = false;
    },

    setFollowUpQuestions: (
      state,
      action: PayloadAction<{
        key: string;
        questions: FollowUpQuestion[];
        messageId?: string;
        sessionId?: string | null;
      }>
    ) => {
      const sid = action.payload.sessionId ?? state.sessionId;
      if (!sid || !Array.isArray(state.messages[sid])) return;
      const msg = state.messages[sid].find(
        (m) => m.key === action.payload.key && m.role === "bot"
      );
      if (msg) {
        msg.followUpQuestions = action.payload.questions;
        if (action.payload.messageId) {
          msg.messageId = action.payload.messageId;
        }
      }
    },

    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
      if (!Array.isArray(state.messages[action.payload]))
        state.messages[action.payload] = [];
    },

    bumpRecentChatReselectNonce: (state) => {
      state.recentChatReselectNonce += 1;
    },

    migrateSessionMessages: (
      state,
      action: PayloadAction<{ fromSessionId: string; toSessionId: string }>
    ) => {
      const { fromSessionId, toSessionId } = action.payload;

      if (Array.isArray(state.messages[fromSessionId])) {
        if (!Array.isArray(state.messages[toSessionId]))
          state.messages[toSessionId] = [];
        state.messages[toSessionId] = [
          ...state.messages[toSessionId],
          ...state.messages[fromSessionId],
        ];
        delete state.messages[fromSessionId];
      }

      if (state.threads[fromSessionId]) {
        const fromThread = state.threads[fromSessionId];
        const existingToThread = state.threads[toSessionId];
        const migratedThread = {
          ...fromThread,
          ...(existingToThread || {}),
          session_id: toSessionId,
        };
        const nextThreads: Record<string, ChatThread> = {};
        for (const [key, thread] of Object.entries(state.threads)) {
          if (key === fromSessionId) {
            nextThreads[toSessionId] = migratedThread;
          } else if (key !== toSessionId) {
            nextThreads[key] = thread;
          }
        }
        state.threads = nextThreads;
      }

      if (state.sessionCollections[fromSessionId]) {
        state.sessionCollections[toSessionId] =
          state.sessionCollections[fromSessionId];
        delete state.sessionCollections[fromSessionId];
      }

      if (state.sessionCollectionFileIds[fromSessionId]) {
        state.sessionCollectionFileIds[toSessionId] =
          state.sessionCollectionFileIds[fromSessionId];
        delete state.sessionCollectionFileIds[fromSessionId];
      }

      if (state.sessionCollectionFileItems[fromSessionId]) {
        state.sessionCollectionFileItems[toSessionId] =
          state.sessionCollectionFileItems[fromSessionId];
        delete state.sessionCollectionFileItems[fromSessionId];
      }

      if (state.loadedSessions[fromSessionId]) {
        state.loadedSessions[toSessionId] = state.loadedSessions[fromSessionId];
        delete state.loadedSessions[fromSessionId];
      }
    },

    setThreads: (state, action: PayloadAction<Record<string, ChatThread>>) => {
      state.threads = action.payload;
    },

    setSessionMessages: (
      state,
      action: PayloadAction<{ sessionId: string; messages: ChatMessage[] }>
    ) => {
      const { sessionId, messages } = action.payload;
      const incoming = Array.isArray(messages) ? messages : [];
      const prevMessages = state.messages[sessionId] || [];

      const followUpsByMessageId = new Map<string, FollowUpQuestion[]>();
      prevMessages.forEach((m) => {
        if (
          m.role === "bot" &&
          m.messageId &&
          Array.isArray(m.followUpQuestions) &&
          m.followUpQuestions.length > 0
        ) {
          followUpsByMessageId.set(m.messageId, m.followUpQuestions);
        }
      });

      const prevLastBotWithFollowUps = [...prevMessages]
        .reverse()
        .find(
          (m) =>
            m.role === "bot" &&
            Array.isArray(m.followUpQuestions) &&
            m.followUpQuestions.length > 0
        );

      let merged = incoming.map((m) => {
        if (
          m.role !== "bot" ||
          (Array.isArray(m.followUpQuestions) && m.followUpQuestions.length > 0)
        ) {
          return m;
        }
        if (m.messageId && followUpsByMessageId.has(m.messageId)) {
          return {
            ...m,
            followUpQuestions: followUpsByMessageId.get(m.messageId),
          };
        }
        return m;
      });

      const mergedBots = merged.filter((m) => m.role === "bot");
      const lastMergedBot = mergedBots[mergedBots.length - 1];
      if (
        prevLastBotWithFollowUps?.followUpQuestions?.length &&
        lastMergedBot &&
        (!lastMergedBot.followUpQuestions ||
          lastMergedBot.followUpQuestions.length === 0)
      ) {
        merged = merged.map((m) =>
          m.key === lastMergedBot.key
            ? {
                ...m,
                followUpQuestions: prevLastBotWithFollowUps.followUpQuestions,
              }
            : m
        );
      }

      state.messages[sessionId] = merged;
      state.sessionId = sessionId;

      merged.forEach((msg) => {
        if (msg.role === "bot" && msg.thought) {
          state.showThought[msg.key] = true;
        }
      });
      // ensure we don't get stuck in loading after history load
      state.loading = false;
    },

    // ✅ merge-safe per-session collection chips
    setSessionCollections: (
      state,
      action: PayloadAction<{
        sessionId: string;
        collections: Array<string | CollectionPair>;
        mode?: "merge" | "replace"; // default merge
      }>
    ) => {
      const { sessionId, collections, mode = "merge" } = action.payload;
      const toPair = (x: any): CollectionPair =>
        typeof x === "string"
          ? { id: x, name: x }
          : { id: x.id, name: x.name ?? x.id };

      if (mode === "replace") {
        state.sessionCollections[sessionId] = collections.map(toPair);
        return;
      }

      const prev = state.sessionCollections[sessionId] ?? [];
      const map = new Map<string, CollectionPair>();
      [...prev, ...collections.map(toPair)].forEach((p) => {
        if (p?.id) map.set(p.id, p);
      });
      state.sessionCollections[sessionId] = Array.from(map.values());
    },

    setSessionCollectionFileIds: (
      state,
      action: PayloadAction<{
        sessionId: string;
        fileIds: string[];
        mode?: "merge" | "replace";
      }>
    ) => {
      const { sessionId, fileIds, mode = "replace" } = action.payload;
      const normalized = (fileIds ?? []).filter(
        (id): id is string => typeof id === "string" && id.trim().length > 0
      );
      if (mode === "replace") {
        state.sessionCollectionFileIds[sessionId] = normalized;
        if (normalized.length === 0) {
          delete state.sessionCollectionFileItems[sessionId];
        }
        return;
      }
      const prev = state.sessionCollectionFileIds[sessionId] ?? [];
      const map = new Set<string>([...prev, ...normalized]);
      state.sessionCollectionFileIds[sessionId] = Array.from(map);
    },

    setSessionCollectionFileItems: (
      state,
      action: PayloadAction<{
        sessionId: string;
        fileItems: CollectionFileItem[];
        mode?: "merge" | "replace";
      }>
    ) => {
      const { sessionId, fileItems, mode = "replace" } = action.payload;
      const normalized = (fileItems ?? [])
        .map((item) => ({
          id: String(item?.id ?? "").trim(),
          name: String(item?.name ?? item?.id ?? "").trim(),
        }))
        .filter((item) => item.id && item.name);

      if (mode === "replace") {
        if (normalized.length === 0) {
          delete state.sessionCollectionFileItems[sessionId];
        } else {
          state.sessionCollectionFileItems[sessionId] = normalized;
        }
        return;
      }

      const prev = state.sessionCollectionFileItems[sessionId] ?? [];
      const map = new Map<string, CollectionFileItem>();
      [...prev, ...normalized].forEach((item) => {
        map.set(item.id, item);
      });
      state.sessionCollectionFileItems[sessionId] = Array.from(map.values());
    },

    /**
     * Hydrate meeting_id / meeting_ids when sessions list omits them; history API may include ids per row.
     * Does not overwrite existing meeting metadata on the thread.
     */
    mergeThreadMeetingMetadata: (
      state,
      action: PayloadAction<{
        sessionId: string;
        meeting_id?: string;
        meeting_ids?: string[];
        meeting_name?: string;
      }>
    ) => {
      const sid = action.payload.sessionId?.trim();
      if (!sid) return;

      const nameIncoming =
        typeof action.payload.meeting_name === "string"
          ? action.payload.meeting_name.trim()
          : "";

      const normalizeIncoming = (): string[] => {
        const ids = action.payload.meeting_ids;
        if (Array.isArray(ids) && ids.length > 0) {
          const uniq: string[] = [];
          const seen = new Set<string>();
          for (const x of ids) {
            const t = String(x).trim();
            if (t && !seen.has(t)) {
              seen.add(t);
              uniq.push(t);
            }
          }
          if (uniq.length > 0) return uniq;
        }
        const mid = action.payload.meeting_id;
        if (mid != null && String(mid).trim() !== "") {
          return [String(mid).trim()];
        }
        return [];
      };

      const incomingIds = normalizeIncoming();
      if (incomingIds.length === 0 && !nameIncoming) return;

      let storageKey = sid;
      if (!state.threads[sid]) {
        const lower = sid.toLowerCase();
        for (const k of Object.keys(state.threads)) {
          if (k.toLowerCase() === lower) {
            storageKey = k;
            break;
          }
        }
      }

      const existing = state.threads[storageKey];
      const hasExistingIds =
        existing &&
        ((existing.meeting_ids?.length ?? 0) > 0 ||
          (existing.meeting_id != null &&
            String(existing.meeting_id).trim() !== ""));

      if (existing) {
        const next: ChatThread = { ...existing };
        if (incomingIds.length > 0 && !hasExistingIds) {
          next.meeting_id = incomingIds[0];
          next.meeting_ids = incomingIds;
        }
        if (nameIncoming) {
          next.meeting_name = nameIncoming;
        }
        state.threads[storageKey] = next;
        if (incomingIds.length > 0) {
          persistSessionMeetingEntry(storageKey, {
            meeting_id: incomingIds[0],
            meeting_ids: incomingIds,
          });
        }
        return;
      }

      state.threads[storageKey] = {
        session_id: storageKey,
        session_name: "Chat",
        ...(incomingIds.length > 0
          ? { meeting_id: incomingIds[0], meeting_ids: incomingIds }
          : {}),
        ...(nameIncoming ? { meeting_name: nameIncoming } : {}),
      };
      if (incomingIds.length > 0) {
        persistSessionMeetingEntry(storageKey, {
          meeting_id: incomingIds[0],
          meeting_ids: incomingIds,
        });
      }
    },

    showPopup: (state, action: PayloadAction<string>) => {
      state.popup = { visible: true, message: action.payload };
    },
    hidePopup: (state) => {
      state.popup = null;
    },
  },

  /* ---------------------- Extra Reducers (async thunks) ---------------------- */
  extraReducers: (builder) => {
    builder
      // onTextChat
      .addCase(onTextChat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(onTextChat.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ?? "Failed to fetch chat message";
      })
      .addCase(onTextChat.fulfilled, (state) => {
        state.loading = false;
      })

      // fetchChatThreads
      .addCase(fetchChatThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatThreads.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch threads";
      })
      .addCase(fetchChatThreads.fulfilled, (state) => {
        state.loading = false;
      })

      // fetchChatHistoryFromSessionApi lifecycle
      .addCase(fetchChatHistoryFromSessionApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatHistoryFromSessionApi.fulfilled, (state, action) => {
        state.loading = false;
        const sessionId = action.meta.arg.sessionId;
        state.loadedSessions[sessionId] = true;
      })
      .addCase(fetchChatHistoryFromSessionApi.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ?? "Failed to fetch session history";
      })

      // fetchMeetingChatHistory (meeting-note chats) — use string types to avoid circular dependency with asyncApi/chat
      .addCase("chat/fetchMeetingChatHistory/pending", (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase("chat/fetchMeetingChatHistory/fulfilled", (state, action) => {
        state.loading = false;
        const arg = (action as { meta?: { arg?: { sessionId?: string; meetingId?: string } } }).meta?.arg;
        const key = arg?.sessionId ?? arg?.meetingId;
        if (key) state.loadedSessions[key] = true;
      })
      .addCase("chat/fetchMeetingChatHistory/rejected", (state, action) => {
        state.loading = false;
        const payload = (action as { payload?: string }).payload;
        state.error = payload ?? "Failed to fetch meeting chat history";
      })

      // onMeetingNotesChat (home screen + same session as meeting-notes API)
      .addCase("chat/meeting-notes-chat/pending", (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase("chat/meeting-notes-chat/fulfilled", (state) => {
        state.loading = false;
      })
      .addCase("chat/meeting-notes-chat/rejected", (state, action) => {
        state.loading = false;
        const payload = (action as { payload?: string }).payload;
        state.error = payload ?? "Meeting notes chat failed";
      });
  },
});

/* ---------------------- Exports ---------------------- */

export const {
  addMessage,
  appendToBotMessage,
  markMessageComplete,
  addUntitledThread,
  setDocumentSearchResults,
  setFollowUpQuestions,
  setSessionId,
  bumpRecentChatReselectNonce,
  migrateSessionMessages,
  setThreads,
  setSessionMessages,
  renameThread,
  deleteThread,
  setShowThought,
  toggleShowThought,
  clearShowThought,
  setShowThoughtTrue,
  setPendingNewChat,
  showPopup,
  hidePopup,
  setSessionCollections, // <-- merge-capable
  setSessionCollectionFileIds,
  setSessionCollectionFileItems,
  mergeThreadMeetingMetadata,
} = chatSlice.actions;

export default chatSlice.reducer;
